import React, {useCallback, useState} from 'react';
import Typography from './Typography';
import {toggleCaseRegistration} from '../features/thunks';
import {useDispatch, useSelector} from 'react-redux';
import {selectCaseById} from '../features/casesSlice';
import {CaseStatuses, PaymentPlanFeatures} from '../constants';
import {useAuth} from '../util/auth';
import {useRerenderInterval} from '../util/hooks/useRerenderInterval';
import {shouldShowJoinCaseButton} from '../util/cases';
import {Button} from '@/components/ui/button';
import {CalendarPlus, Loader2} from 'lucide-react';
import {useAddToCalendar} from '@/components/AddCaseToCalendarLink';
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from '@/components/ui/tooltip';
import {toast} from '@/components/ui/use-toast';

type RegisterButtonProps = {
    caseId: string;
    layout?: 'horizontal' | 'vertical';
    textRegister?: string;
    textInterested?: string;
    textJoin?: string;
    onRegisterClick?: (caseId: string) => void;
    onUnregisterClick?: (caseId: string) => void;
    [key: string]: unknown;
}

const RegisterButton = ({
    caseId,
    textRegister = 'Register',
    textInterested = 'I am interested',
    textJoin = 'Join the session',
    onRegisterClick,
    onUnregisterClick,
}: RegisterButtonProps) => {
    const currentCase = useSelector((state) => selectCaseById(state, caseId));
    const dispatch = useDispatch();
    const auth = useAuth();
    const handleAddToCalendar = useAddToCalendar(caseId);
    const [loading, setLoading] = useState(false);
    //we rerender components to properly show join case button
    useRerenderInterval(30000)

    if (!currentCase) {
        return null;
    }

    const userId = auth.user ? auth.user.id : undefined;

    const {user_participant, zoom_us_link, scheduled_to, register_max, register_count, duration} = currentCase;

    const showJoinButton = shouldShowJoinCaseButton({
        scheduled_to,
        user_participant,
        zoom_us_link,
        duration,
    });

    const showUnregisterButton = !showJoinButton && user_participant;
    const showRegisterButton = !user_participant;
    const isCaseAlreadyStarted = scheduled_to && scheduled_to < Date.now() / 1000;
    const isExpired = !!(scheduled_to && (scheduled_to + ((duration || 90) * 60)) < Date.now() / 1000);

    const isTentative = currentCase.state === CaseStatuses.TENTATIVE;
    const isPartOfSubscription = (auth.isAuthenticated && auth.isIncludedInSubscription(PaymentPlanFeatures.live_sessions));
    const isRegistrationDisabled = isCaseAlreadyStarted || !isPartOfSubscription;
    const isUnregisterDisabled = isExpired;

    const handleError = useCallback((response: {
        error?: {
            message: string;
        }
    }) => {
        setLoading(false);
        if (response?.error?.message) {
            alert(response.error.message);
        }
    }, []);

    if (showUnregisterButton) {
        return (
            <div className="flex gap-1">
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button variant="outline" size="icon" onClick={handleAddToCalendar}>
                                <CalendarPlus className="w-4 h-4" />
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                            <p>Add to calendar</p>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
                <Button variant="outline" disabled={loading || isUnregisterDisabled} size="lg" onClick={() => {
                    setLoading(true);
                    dispatch(toggleCaseRegistration({caseId, userId})).then(handleError).catch(() => {
                        setLoading(false);
                    });
                    onUnregisterClick && onUnregisterClick(caseId);
                }}>
                    {loading ? <Loader2 className="animate-spin" /> : null}
                    Unregister
                </Button>
            </div>
        )
    }

    if (showJoinButton) {
        return (
            <Button
                disabled={!auth.isIncludedInSubscription(PaymentPlanFeatures.live_sessions)}
                variant={!showJoinButton ? 'outline' : 'default'}
                size="lg"
                asChild
            >
                <a href={zoom_us_link}>
                    {textJoin}
                </a>
            </Button>
        )
    }


    const isAllSpotsTaken = (register_count && register_max) && register_count >= register_max;

    if (showRegisterButton) {
        return (
            <Button
                disabled={isRegistrationDisabled || loading}
                size="lg"
                onClick={() => {
                    if (isAllSpotsTaken) {
                        toast({
                            title: "All seats taken",
                            description: "All seats are currently taken. Please check back later to see if any seats become available.",
                        })
                        return;
                    }

                    if (isCaseAlreadyStarted) {
                        toast({
                            title: "Case already started",
                            description: "You can't register for a case that has already started",
                        })
                        return;
                    }

                    if (auth.isAuthenticated) {
                        setLoading(true);
                        // @ts-ignore
                        dispatch(toggleCaseRegistration({caseId, userId})).then(handleError).catch(() => {
                            setLoading(false);
                        });
                        onRegisterClick && onRegisterClick(caseId);
                    } else {
                        auth.redirectToLogin({next : `/case/${caseId}`});
                    }
                }}
            >
                {loading ? <Loader2 className="animate-spin" /> : null}
                {isAllSpotsTaken ? 'All seats taken' : isTentative ? textInterested : textRegister}
            </Button>
        )
    }

    return null;
};

export default RegisterButton;
